import axios from 'axios'
import { API } from '../../../common/api'
import { labravAWSEngineServiceUrl } from '../../../common/utils/constants'

// Unauthenticated axios instance for public OTP endpoints opened via email links.
// The shared `API` instance auto-attaches `Authorization: Bearer <token>` which the
// backend rejects for these public routes.
const publicApi = axios.create({ timeout: 30000 })

export const fetchPartnerIqTableData = (
  partnerId: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  segment?: string,
  icpFitMin?: number,
  icpFitMax?: number,
  title?: string,
  contactName?: string,
  location?: string
) =>
  API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts`, {
    params: {
      sort_by: sortBy,
      sort_order: sortOrder,
      segment: segment,
      icp_fit_min: icpFitMin,
      icp_fit_max: icpFitMax,
      title: title,
      contact_name: contactName,
      location: location,
    },
  })

export const fetchPartnerIqOverviewData = (partnerId: string) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/overview`
  )

export const putPartnerIqIcpFormData = (partnerId: string, data: object) =>
  API.put(`${labravAWSEngineServiceUrl}/partners/${partnerId}/icp`, data)

export const fetchPartnerIqIcpFormData = (partnerId: string) =>
  API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/icp`)

export const fetchPartnerIqProfileData = (
  partnerId: string,
  contactId: string
) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/${contactId}/profile`
  )

export const fetchPartnerIqInvitationDetails = (
  partnerId: string,
  contactId: string,
  signal?: AbortSignal
) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/${contactId}/invitation_details`,
    { signal }
  )

export const resendCollabRoomOtp = (invitationId: string) =>
  publicApi.get(`${labravAWSEngineServiceUrl}/collab-room/${invitationId}`, {
    params: { resend: true },
  })

export const getVerifyCollabRoomOtpUrl = (invitationId: string, otp: string) =>
  `${labravAWSEngineServiceUrl}/collab-room/${invitationId}/otp/${encodeURIComponent(
    otp
  )}`

export const defaultCollabRoomInvalidOtpMessage =
  'Wrong verification code entered. Please try again!'

function collabRoomOtpErrorMessageFromPayload(
  data: unknown,
  fallback: string
): string {
  if (data == null) return fallback
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed) return fallback
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return collabRoomOtpErrorMessageFromPayload(
          JSON.parse(trimmed),
          fallback
        )
      } catch {
        return trimmed.length > 400 ? fallback : trimmed
      }
    }
    return trimmed.length > 400 ? fallback : trimmed
  }
  if (typeof data === 'object') {
    const record = data as Record<string, unknown>
    for (const key of [
      'message',
      'detail',
      'error',
      'errorMessage',
      'description',
    ] as const) {
      const value = record[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
    const errors = record.errors
    if (Array.isArray(errors)) {
      const parts = errors.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0
      )
      if (parts.length > 0) return parts.join(', ')
    }
  }
  return fallback
}

async function collabRoomOtpVerifyErrorMessageFromResponse(
  response: Response,
  fallback: string
): Promise<string> {
  const contentType = response.headers.get('content-type') ?? ''
  try {
    if (contentType.includes('application/json')) {
      const data: unknown = await response.json()
      return collabRoomOtpErrorMessageFromPayload(data, fallback)
    }
    const text = (await response.text()).trim()
    return collabRoomOtpErrorMessageFromPayload(text, fallback)
  } catch {
    return fallback
  }
}

export type SubmitVerifyCollabRoomOtpResult =
  | { type: 'redirect'; location: string }
  | { type: 'invalid_otp'; message: string }

/**
 * Probes the verify URL with fetch + redirect: 'manual'.
 * - 3xx (or opaque redirect): navigate to Location when exposed, else the verify URL (full navigation fallback).
 * - 2xx: treat as success and use response.url (final URL if the runtime followed).
 * - Other non-OK: surface API error text when present, else default copy.
 *
 * For cross-origin calls, the engine should expose `Location` via
 * Access-Control-Expose-Headers so the SPA can read redirect targets.
 */
export const submitVerifyCollabRoomOtp = async (
  invitationId: string,
  otp: string
): Promise<SubmitVerifyCollabRoomOtpResult> => {
  const verifyUrl = getVerifyCollabRoomOtpUrl(invitationId, otp)
  let response: Response
  try {
    response = await fetch(verifyUrl, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'manual',
    })
  } catch {
    return {
      type: 'invalid_otp',
      message: 'Verification failed. Please try again.',
    }
  }

  if (response.type === 'opaqueredirect') {
    return { type: 'redirect', location: verifyUrl }
  }

  const status = response.status
  if (status >= 300 && status < 400) {
    const rawLocation = response.headers.get('Location')
    if (rawLocation?.trim()) {
      return {
        type: 'redirect',
        location: new URL(rawLocation.trim(), verifyUrl).href,
      }
    }
    return { type: 'redirect', location: verifyUrl }
  }

  if (response.ok) {
    return { type: 'redirect', location: response.url || verifyUrl }
  }

  await collabRoomOtpVerifyErrorMessageFromResponse(
    response,
    defaultCollabRoomInvalidOtpMessage
  )
  return { type: 'invalid_otp', message: defaultCollabRoomInvalidOtpMessage }
}

export const verifyCollabRoomOtp = (invitationId: string, otp: string) =>
  publicApi.get(getVerifyCollabRoomOtpUrl(invitationId, otp))

export const fetchPartnerIqSegments = (partnerId: string) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/segments`
  )

export const fetchPartnerIqMetrics = (partnerId: string) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/partner_info/metrics`
  )

export const fetchPartnerIqTableReport = (
  partnerId: string,
  page?: number,
  pageSize?: number,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  segment?: string,
  icpFitMin?: number,
  icpFitMax?: number,
  contactName?: string,
  title?: string,
  location?: string
) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/download`,
    {
      params: {
        page: page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        segment: segment,
        icp_fit_min: icpFitMin,
        icp_fit_max: icpFitMax,
        contact_name: contactName,
        title: title,
        location: location,
      },
      responseType: 'blob',
    }
  )

export const fetchPartnerIqOpportunitiesReport = (
  partnerId: string,
  contactId: string
) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/${contactId}/opportunities/download`,
    {
      responseType: 'blob',
    }
  )

/** Collab Room / Admin Panel page data (company info, trusted by, metrics, etc.) */
export const fetchCollabRoomPageData = (partnerId: string) =>
  API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/partner_info/`)

/** Create partner info (POST when no data exists yet) */
export const createCollabRoomPageData = (
  partnerId: string,
  data: Record<string, unknown>
) =>
  API.post(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/partner_info/`,
    data
  )

/** Send a collab room invite for a contact */
export const postCollabInvite = (
  partnerId: string,
  contactId: string,
  userId: string
) =>
  API.post(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/contacts/${contactId}/collab-invite`,
    { user_id: userId }
  )

/** Update partner info (PUT when data already exists) */
export const saveCollabRoomPageData = (
  partnerId: string,
  data: Record<string, unknown>
) =>
  API.put(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/partner_info/`,
    data
  )
